import React from "react";
import ChartHeader from "../controls/ChartHeader";
import styles from "../../analysis/Charts.module.css";
import layoutStyles from "../../../../styles/utilities/layout.module.css";
import chartStyles from "../../../../styles/components/charts.module.css";

export interface DualChartLayoutProps {
  volumeData: any;
  countData: any;
  VolumeChart: React.ComponentType<any>;
  CountChart: React.ComponentType<any>;
  volumeProps?: Record<string, any>;
  countProps?: Record<string, any>;
  volumeTitle?: string;
  countTitle?: string;
  volumeSubtitle?: string;
  countSubtitle?: string;
  onVolumeExpand?: () => void;
  onCountExpand?: () => void;
  gridClassName?: string;
  mobileShowOnly?: string | null; // 'volume' | 'count' | null
  showHeaders?: boolean;
  children?: React.ReactNode;
}

const DualChartLayout: React.FC<DualChartLayoutProps> = ({
  volumeData,
  countData,
  VolumeChart,
  CountChart,
  volumeProps = {},
  countProps = {},
  volumeTitle = "Invested capital",
  countTitle = "Number of deals",
  volumeSubtitle = "in CHF Mio.",
  countSubtitle = "",
  onVolumeExpand,
  onCountExpand,
  gridClassName = `${layoutStyles.grid} ${layoutStyles.gridCols1} ${layoutStyles.mdGridCols2} ${layoutStyles.gridGap15}`,
  mobileShowOnly = null,
  showHeaders = true,
  children,
}) => {
  const showVolume = mobileShowOnly === null || mobileShowOnly === "volume";
  const showCount = mobileShowOnly === null || mobileShowOnly === "count";

  const volumeChartClass =
    styles.chartArea +
    " " +
    (mobileShowOnly === "volume"
      ? chartStyles.chartAreaLeftActive
      : mobileShowOnly === "count"
        ? chartStyles.chartAreaRightActive
        : "");

  const countChartClass =
    styles.chartArea +
    " " +
    (mobileShowOnly === "count"
      ? chartStyles.chartAreaRightActive
      : mobileShowOnly === "volume"
        ? chartStyles.chartAreaLeftActive
        : "");

  return (
    <div className={layoutStyles.spaceY4}>
      {children}

      <div className={gridClassName}>
        {/* Volume Chart */}
        {showVolume && (
          <div className={volumeChartClass}>
            {showHeaders && (
              <div className={styles.chartHeaderOverlay}>
                <ChartHeader
                  title={volumeTitle}
                  subtitle={volumeSubtitle}
                  showExpandButton={!!onVolumeExpand}
                  onExpand={onVolumeExpand}
                />
              </div>
            )}
            <VolumeChart data={volumeData} isVolume={true} {...volumeProps} />
          </div>
        )}

        {/* Count Chart */}
        {showCount && (
          <div className={countChartClass}>
            {showHeaders && (
              <div className={styles.chartHeaderOverlay}>
                <ChartHeader
                  title={countTitle}
                  subtitle={countSubtitle}
                  showExpandButton={!!onCountExpand}
                  onExpand={onCountExpand}
                />
              </div>
            )}
            <CountChart data={countData} isVolume={false} {...countProps} />
          </div>
        )}
      </div>
    </div>
  );
};

export interface SingleChartLayoutProps {
  data: any;
  ChartComponent: React.ComponentType<any>;
  chartProps?: Record<string, any>;
  title: string;
  onExpand?: () => void;
  isVolumeChart?: boolean;
  className?: string;
  showHeader?: boolean;
  children?: React.ReactNode;
}

const SingleChartLayout: React.FC<SingleChartLayoutProps> = ({
  data,
  ChartComponent,
  chartProps = {},
  title,
  onExpand,
  isVolumeChart = false,
  className = "",
  showHeader = true,
  children,
}) => {
  return (
    <div className={className || layoutStyles.spaceY4}>
      {children}
      <div>
        {showHeader && (
          <div className={styles.chartHeaderOverlay}>
            <ChartHeader
              title={title}
              subtitle={""}
              showExpandButton={!!onExpand}
              onExpand={onExpand}
            />
          </div>
        )}
        <ChartComponent data={data} {...chartProps} />
      </div>
    </div>
  );
};

export { DualChartLayout, SingleChartLayout };
